import React from "react";
import {
  useBackgroundJobStatusQuery,
  type BackgroundJobStatusQuery,
} from "~/graphql/generated/hooks";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@components/ui/hover-card";
import { Skeleton } from "@components/ui/skeleton";
import { cn } from "~/lib/utils";
import {
  SIDEBAR_JOB_STATUS_POLL_INTERVAL_MS,
  formatDuration,
  formatEta,
  formatJobCount,
  formatScheduledAt,
  getJobProgressPercent,
} from "./jobStatusFormat";
import {
  Activity,
  AlertCircle,
  Clock,
  ListTodo,
  LoaderCircle,
} from "lucide-react";

type BackgroundJobStatus = BackgroundJobStatusQuery["backgroundJobStatus"];

interface JobStatusIndicatorProps {
  className?: string;
}

export const JobStatusIndicator: React.FC<JobStatusIndicatorProps> = ({
  className,
}) => {
  const { data, loading, error } = useBackgroundJobStatusQuery({
    fetchPolicy: "cache-and-network",
    pollInterval: SIDEBAR_JOB_STATUS_POLL_INTERVAL_MS,
  });

  const status = data?.backgroundJobStatus;

  if (error) {
    return (
      <div
        className={cn(
          "mx-2 rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground group-data-[collapsible=icon]:mx-1 group-data-[collapsible=icon]:px-2",
          className
        )}
        aria-label="Background jobs unavailable"
      >
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <AlertCircle className="size-4" />
          <span className="group-data-[collapsible=icon]:hidden">
            Jobs unavailable
          </span>
        </div>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div
        className={cn(
          "mx-2 rounded-md border px-3 py-2 group-data-[collapsible=icon]:mx-1 group-data-[collapsible=icon]:px-2",
          className
        )}
        aria-label="Background jobs loading"
      >
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <Activity className="size-4 text-muted-foreground" />
          <div className="grid flex-1 gap-1 group-data-[collapsible=icon]:hidden">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const hasRunningJobs = status.processingCount > 0;

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative mx-2 overflow-hidden rounded-md border px-3 py-2 text-left transition-colors hover:bg-sidebar-accent group-data-[collapsible=icon]:mx-1 group-data-[collapsible=icon]:px-2",
            hasRunningJobs && "border-primary/40 bg-sidebar-accent/50",
            className
          )}
          aria-label={`Background jobs: ${status.processingCount} processing, ${status.queuedCount} queued`}
        >
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
            {hasRunningJobs ? (
              <LoaderCircle className="size-4 animate-spin text-primary" />
            ) : (
              <Activity className="size-4 text-muted-foreground" />
            )}
            <div className="grid flex-1 gap-1 group-data-[collapsible=icon]:hidden">
              <span className="flex items-center gap-2 text-xs font-medium">
                Background jobs
                {hasRunningJobs ? (
                  <span className="relative flex size-2" aria-hidden="true">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-primary" />
                  </span>
                ) : null}
              </span>
              <span className="text-xs text-muted-foreground">
                {status.processingCount} processing / {status.queuedCount} queued
              </span>
            </div>
          </div>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="end"
        collisionPadding={8}
        className="w-[min(24rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] p-0"
      >
        <JobStatusPopup status={status} />
      </HoverCardContent>
    </HoverCard>
  );
};

const JobStatusPopup: React.FC<{ status: BackgroundJobStatus }> = ({
  status,
}) => (
  <div className="grid min-w-0 gap-4 p-4">
    <div className="flex min-w-0 items-center justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold">Background jobs</h3>
        <p className="text-xs text-muted-foreground">
          {status.scheduledCount} scheduled
        </p>
      </div>
      <div className="flex shrink-0 gap-2 text-xs">
        <span className="rounded-md bg-muted px-2 py-1">
          {status.processingCount} running
        </span>
        <span className="rounded-md bg-muted px-2 py-1">
          {status.queuedCount} queued
        </span>
      </div>
    </div>

    <section className="grid gap-2">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Clock className="size-3.5" />
        <span>Running now</span>
      </div>
      {status.processingJobs.length > 0 ? (
        <div className="grid gap-2">
          {status.processingJobs.map((job) => {
            const progressPercent = getJobProgressPercent(
              job.elapsedSeconds,
              job.averageDurationSeconds
            );

            return (
              <div
                key={job.jobId}
                className="relative min-w-0 overflow-hidden rounded-md border bg-background px-3 py-2"
              >
                <div className="flex min-w-0 items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
                    <LoaderCircle className="size-3.5 shrink-0 animate-spin text-primary" />
                    <span className="truncate">{job.displayName}</span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    ETA: {formatEta(job.etaSeconds)}
                  </span>
                </div>
                <div className="mt-1 flex min-w-0 items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span className="truncate">Job: {job.methodType}</span>
                  <span className="shrink-0">
                    Elapsed: {formatDuration(job.elapsedSeconds)}
                  </span>
                </div>
                {progressPercent !== null ? (
                  <div
                    className="mt-2 h-1 overflow-hidden rounded-full bg-primary/15"
                    aria-label={`Estimated progress: ${progressPercent}%`}
                  >
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          No jobs are currently running.
        </p>
      )}
    </section>

    <section className="grid gap-2">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <ListTodo className="size-3.5" />
        <span>Queued by type</span>
      </div>
      {status.queuedByType.length > 0 ? (
        <div className="grid gap-2">
          {status.queuedByType.map((item) => (
            <div
              key={`${item.methodType}:${item.queue}`}
              className="flex min-w-0 items-center justify-between gap-3 rounded-md border bg-background px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {item.displayName}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {item.queue === "scheduled"
                    ? formatScheduledAt(item.nextScheduledAt)
                    : `Queue: ${item.queue}`}{" "}
                  / Queue ETA: {formatEta(item.estimatedTotalDurationSeconds)}
                </div>
              </div>
              <span className="shrink-0 rounded-md bg-muted px-2 py-1 text-xs">
                {formatJobCount(item.count)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
          No jobs are queued.
        </p>
      )}
    </section>
  </div>
);
